import { useState } from "react";
import { X, Loader2, AlertCircle, ArrowRightLeft, Undo2 } from "lucide-react";
import {
  useAdminReassignCommitteeMutation,
  useAdminUnassignCommitteeMutation,
} from "../api/upgradeApi";
import { describeApiError } from "../../../lib/apiError";

const ROLE_LABEL: Record<string, string> = {
  chairman: "Chairman",
  secretary: "Secretary",
  member: "Member",
};

/**
 * Move one application off a committee member who is not going to assess it.
 *
 * Two ways out, because they are the same decision taken two ways: name who
 * will do it instead, or put it back for the secretariat to place again. Both
 * are offered here rather than the second being a separate press elsewhere —
 * having opened this dialog the admin may well find nobody free, and closing it
 * to look for another button is a dead end.
 *
 * The panel is listed with each member's current load, because that is the
 * question being answered. Work is moved off somebody who has stalled, and
 * moving it onto the next most loaded person is how it stalls again. The person
 * holding it now is listed too, disabled, so the list does not silently omit
 * them and leave the reader wondering whether they scrolled past.
 */
export default function ReassignCommitteeReviewModal({
  applicationId,
  applicantName,
  review,
  panel,
  onClose,
  onDone,
}: {
  applicationId: string | number;
  applicantName?: string;
  /** The assignment row being moved: { id, member_id, name, decision }. */
  review: any;
  /** The cycle's panel, as the screen already has it. */
  panel: any[];
  onClose: () => void;
  onDone?: (message: string) => void;
}) {
  const [err, setErr] = useState<string | null>(null);

  const [reassign, { isLoading: moving }] = useAdminReassignCommitteeMutation();
  const [unassign, { isLoading: withdrawing }] = useAdminUnassignCommitteeMutation();

  const busy = moving || withdrawing;

  // Former members keep the work they already hold but take no new — the same
  // rule the "Assign to…" select applies, and the one the server enforces.
  const candidates = panel.filter((m: any) => !m.is_former);

  const run = async (fn: () => Promise<any>, fallback: string) => {
    setErr(null);
    try {
      const res: any = await fn();
      onDone?.(res?.message ?? fallback);
      onClose();
    } catch (e: any) {
      setErr(describeApiError(e) || "That did not work. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 md:p-8">
      <div className="w-full max-w-lg bg-white rounded-[28px] shadow-2xl border border-gray-100 overflow-hidden">
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-2xl flex items-center justify-center">
              <ArrowRightLeft className="w-5 h-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-black text-gray-900 tracking-tight truncate">
                Move {applicantName || "this application"}
              </h2>
              <p className="text-xs font-semibold text-gray-500">
                {review?.name ? `With ${review.name} now` : "No reviewer recorded"} · pick who
                assesses it instead
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-50 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-4 max-h-[55vh] overflow-y-auto">
          {candidates.length === 0 ? (
            <p className="text-sm text-gray-500 p-2">
              Nobody on the Membership Committee can take new work. Put it back in the pool below
              instead.
            </p>
          ) : (
            <div className="space-y-1.5">
              {candidates.map((m: any) => {
                const holdsIt = String(m.member_id) === String(review?.member_id ?? "");

                return (
                  <button
                    key={m.member_id}
                    type="button"
                    disabled={busy || holdsIt}
                    onClick={() =>
                      run(
                        () =>
                          reassign({
                            id: applicationId,
                            reviewId: review.id,
                            member_id: m.member_id,
                          }).unwrap(),
                        "Moved.",
                      )
                    }
                    className="w-full flex items-center justify-between gap-3 text-left px-4 py-3 rounded-2xl border border-gray-100 hover:border-blue-300 hover:bg-blue-50/40 disabled:opacity-50 disabled:hover:border-gray-100 disabled:hover:bg-transparent transition"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 truncate">{m.name}</div>
                      <div className="text-[11px] font-semibold text-gray-400">
                        {[ROLE_LABEL[m.committee_role] ?? m.committee_role, m.membership_no]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                    {holdsIt ? (
                      <span className="shrink-0 text-[10px] font-black uppercase tracking-widest text-gray-400">
                        Has it now
                      </span>
                    ) : (
                      <span
                        className={`shrink-0 inline-flex items-center px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                          m.pending_count > 0
                            ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-gray-50 text-gray-400 border-gray-200"
                        }`}
                      >
                        {m.pending_count ?? 0} pending
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {err ? (
            <div className="mt-3 flex gap-2 p-3 rounded-2xl bg-rose-50 border border-rose-200 text-[12px] font-semibold text-rose-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>{err}</div>
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/60">
          {/* Not a third icon in the row: "I do not know who yet" is the
              commoner answer when a reviewer has gone quiet, so it belongs
              beside the names rather than behind another press. */}
          <button
            type="button"
            onClick={() =>
              run(
                () => unassign({ id: applicationId, reviewId: review.id }).unwrap(),
                "Assignment withdrawn.",
              )
            }
            disabled={busy}
            title="Take it off this member and leave the application unassigned"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-amber-200 text-amber-700 text-xs font-bold hover:bg-amber-50 disabled:opacity-50"
          >
            {withdrawing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Undo2 className="w-3.5 h-3.5" />
            )}
            Withdraw the assignment
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-bold text-gray-500 hover:bg-white"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
